/**
 * Sprint 11.12 \u2014 Single source of truth for converting the live
 * `/api/v1/client-report/{token}` (and `/candidates/{cid}/client-report-preview`)
 * payload into the `MagazineReport` data shape used by both the public
 * client-report page and the in-portal "Client Report" tab.
 *
 * Both endpoints return identical shapes \u2014 only difference is the public
 * one honours `included_artefacts` while the preview unlocks all of them.
 */

import { parseServerDate } from '@/lib/format';
import { CLIENT_ARTEFACT_UNAVAILABLE_MSG } from '@/lib/evalErrors';

const VERDICT_THRESHOLDS_5 = { strong: 4, recommend: 3.2, neutral: 2.5 };

function buildMcqLikeSection(items, stageBlock) {
  if (!items.length && !stageBlock) return null;
  const answered = items.filter((m) => m.selected_index != null);
  const correct = answered.filter((m) => m.is_correct).length;
  const total = items.length;
  const skipped = items.length - answered.length;
  const score_pct = total > 0
    ? Math.round((correct / total) * 100)
    : (stageBlock?.overall_score ?? 0);
  const byDiff = {};
  items.forEach((m) => {
    const lvl = m.difficulty || 'Mixed';
    byDiff[lvl] = byDiff[lvl] || { level: lvl, correct: 0, total: 0 };
    byDiff[lvl].total += 1;
    if (m.is_correct) byDiff[lvl].correct += 1;
  });
  const byTopic = {};
  items.forEach((m) => {
    const topic = m.topic || 'General';
    byTopic[topic] = byTopic[topic] || { name: topic, correct: 0, total: 0, sample_topics: new Set() };
    byTopic[topic].total += 1;
    if (m.is_correct) byTopic[topic].correct += 1;
    if (m.difficulty) byTopic[topic].sample_topics.add(m.difficulty);
  });
  return {
    result: {
      score_pct,
      correct,
      skipped,
      total_questions: total || correct,
      percentile: null,
      pool_size: 0,
      difficulty_breakdown: Object.values(byDiff).sort((a, b) => {
        const order = { Easy: 0, Medium: 1, Hard: 2, Mixed: 3 };
        return (order[a.level] ?? 9) - (order[b.level] ?? 9);
      }),
    },
    categories: Object.values(byTopic).map((b) => ({
      name: b.name,
      total: b.total,
      correct: b.correct,
      sample_topics: Array.from(b.sample_topics).slice(0, 3),
    })),
    items,
    eval: stageBlock ? {
      verdict: stageBlock.verdict,
      eval_summary: stageBlock.eval_summary,
      strengths: stageBlock.strengths || [],
      weaknesses: stageBlock.weaknesses || [],
      concepts_strong: stageBlock.concepts_strong || [],
      concepts_weak: stageBlock.concepts_weak || [],
      red_flag_detected: stageBlock.red_flag_detected,
      red_flag_reason: stageBlock.red_flag_reason,
    } : null,
  };
}

function recForScore(score) {
  if (score == null) return 'Pending';
  if (score >= VERDICT_THRESHOLDS_5.strong) return 'Strong Recommend';
  if (score >= VERDICT_THRESHOLDS_5.recommend) return 'Recommend';
  if (score >= VERDICT_THRESHOLDS_5.neutral) return 'Neutral';
  return 'Not Recommended';
}

export function adaptLivePayloadToMagazine(api) {
  const c = api.candidate || {};
  const link = api.link || {};
  const isStaffPreview = !!link.is_staff_preview;
  const included = new Set(link.included_artefacts || []);
  const unavailable = api.artefact_unavailable || {};
  const softMsg = (key) => unavailable[key] || CLIENT_ARTEFACT_UNAVAILABLE_MSG;
  const rawPanels = (!isStaffPreview && included.size > 0 && !included.has('panels'))
    ? []
    : (api.panels || []);
  const includeAiReport = isStaffPreview || included.size === 0 || included.has('ai_report');

  const companyResumePayload = api.company_resume?.payload;
  const candidate = {
    id: c.id,
    full_name: c.full_name || 'Candidate',
    role_label: c.role_code || c.role_label || null,
    department_label: c.department_code || c.department_label || null,
    current_stage_code: c.current_stage_code,
    handle: c.handle,
    photo_url: c.photo_url || null,
    resume: companyResumePayload?.summary
      ? { summary: companyResumePayload.summary, key_skills: [] }
      : null,
  };

  const vbChapters = api.videobot_chapters || [];
  const vbBlock = api.videobot || null;
  // Staff preview and public share use the same curated set so Evaluation
  // Summary / Video Bot scores stay identical. Legacy rows defaulted
  // client_curated=0; if nothing is curated yet, fall back to all chapters.
  const curatedOnly = vbChapters.filter((ch) => ch.client_curated !== false);
  const chaptersForReport = curatedOnly.length > 0 ? curatedOnly : vbChapters;
  let videobot = (chaptersForReport.length === 0 && !vbBlock) ? null : {
    responses: chaptersForReport.map((ch) => ({
      question_id: ch.question_id,
      question_text: ch.question_text,
      video_url: ch.video_url || null,
      transcript: ch.transcript || null,
      duration_seconds: ch.duration_sec || 0,
      overall_score: ch.overall_score != null ? ch.overall_score : null,
      human_overall_score: ch.human_overall_score != null ? ch.human_overall_score : null,
      clarity_score: ch.clarity_score,
      depth_score: ch.depth_score,
      structure_score: ch.structure_score,
      energy_score: ch.energy_score,
      concepts_detected: ch.concepts_detected || [],
      concepts_missing: ch.concepts_missing || [],
      eval_summary: ch.eval_summary || '',
      verdict: ch.verdict,
      client_curated: ch.client_curated !== false,
    })),
    eval: vbBlock ? {
      verdict: vbBlock.verdict,
      eval_summary: vbBlock.eval_summary,
      strengths: vbBlock.strengths || [],
      weaknesses: vbBlock.weaknesses || [],
      overall_score: vbBlock.overall_score,
      verdict_tone: vbBlock.verdict_tone,
    } : null,
  };
  if (!isStaffPreview && unavailable.video_bot) {
    videobot = { unavailable: true, message: softMsg('video_bot') };
  }

  const mcqItems = api.mcq_items || [];
  const mcqBlock = api.mcq;
  let mcq = buildMcqLikeSection(mcqItems, mcqBlock);
  if (!isStaffPreview && unavailable.mcq) {
    mcq = { unavailable: true, message: softMsg('mcq') };
  }

  const techItems = api.tech_assessment_items || [];
  const techBlock = api.tech_assessment;
  let tech_assessment = buildMcqLikeSection(techItems, techBlock);
  if (!isStaffPreview && unavailable.tech_assessment) {
    tech_assessment = { unavailable: true, message: softMsg('tech_assessment') };
  }

  const adaptedPanels = rawPanels.map((p) => {
    // Ratings are manual panellist feedback only. ai_report artefact gates
    // optional narrative summary text — never AI overall / spider as ratings.
    const ev = p.evaluation || null;
    const factor_scores = Array.isArray(p.factor_scores) && p.factor_scores.length
      ? p.factor_scores
      : null;
    const humanOverall = p.human_overall_score != null ? p.human_overall_score : null;
    const hasManualRatings = humanOverall != null || !!factor_scores?.length;
    const summaryText = includeAiReport && hasManualRatings
      ? (p.ai_summary || ev?.summary || null)
      : null;
    const humanSpider = (factor_scores || []).map((f) => ({
      dimension: f.name,
      score: f.score,
      fullMark: 5,
    }));
    return {
      id: p.id,
      label: p.pipeline_stage_label
        ? p.pipeline_stage_label
        : `Panel \u00b7 ${p.subject}`,
      code_editor_enabled: false,
      code_output: null,
      recording_url: p.recording_url || null,
      recording_artifact_id: p.recording_artifact_id || null,
      transcript: [],
      transcript_text: p.transcript_text || null,
      human_overall_score: humanOverall,
      factor_scores,
      feedback_pending: !!p.feedback_pending,
      interview_questions: (p.interview_questions || []).map((q) => ({
        question_text: q.question_text,
        star_score: q.star_score != null ? q.star_score : humanOverall,
      })),
      ai_summary: summaryText,
      ai_report: hasManualRatings ? {
        overall_score: humanOverall,
        overall_recommendation: recForScore(humanOverall),
        summary_narrative: {
          strengths: null,
          development_areas: null,
          overall_assessment: summaryText,
        },
        spider: humanSpider,
      } : null,
    };
  });

  const offer = api.offer || null;
  let finalReport = api.final_report || null;
  if (!isStaffPreview && unavailable.final_report) {
    finalReport = { unavailable: true, message: softMsg('final_report') };
  }
  const hr_evaluation = (isStaffPreview || included.has('offer'))
    ? (api.hr_evaluation || null)
    : null;

  let panelsOut = adaptedPanels;
  if (!isStaffPreview && (unavailable.panels || unavailable.ai_report || unavailable.ai_breakdown)) {
    panelsOut = [{
      unavailable: true,
      message: softMsg(unavailable.panels ? 'panels' : (unavailable.ai_report ? 'ai_report' : 'ai_breakdown')),
    }];
  }

  let companyResume = api.company_resume
    ? {
        payload: api.company_resume.payload || {},
        generated_at: api.company_resume.generated_at,
        approved: api.company_resume.approved !== false,
        template: api.company_resume.template_snapshot || {},
      }
    : null;
  if (!isStaffPreview && unavailable.resume) {
    companyResume = { unavailable: true, message: softMsg('resume') };
  }

  // Staff-only Recommendation body. Never trust public payloads even if leaked.
  let recommendation = null;
  if (isStaffPreview && api.recommendation) {
    const rec = api.recommendation;
    const finalCall = rec.final_call || hr_evaluation?.final_call || null;
    recommendation = {
      status: rec.status || 'pending',
      final_call: finalCall,
      final_call_label: rec.final_call_label || null,
      subtitle: rec.subtitle || null,
      grounded_in_data: rec.grounded_in_data || null,
      watch_outs: rec.watch_outs || null,
      learning_plan_intro: rec.learning_plan_intro || null,
      learning_plan: rec.learning_plan || null,
      next_steps: rec.next_steps || [],
      model_name: rec.model_name || null,
      rubric_version: rec.rubric_version || null,
      error_text: rec.error_text || null,
      composite_pct: rec.composite_pct ?? null,
      updated_at: rec.updated_at || null,
    };
  }

  const expires = link.expires_at ? parseServerDate(link.expires_at) : null;
  const expiry_days = expires
    ? Math.max(0, Math.ceil((expires.getTime() - Date.now()) / (24 * 3600 * 1000)))
    : null;

  const hasTranscript = adaptedPanels.some((p) => !!(p.transcript_text || '').trim());

  return {
    candidate,
    videobot,
    mcq,
    tech_assessment,
    panels: panelsOut,
    offer,
    hr_evaluation,
    recommendation,
    final_report: finalReport,
    company_resume: companyResume,
    link: {
      expires_at: link.expires_at,
      issued_at: link.issued_at,
      expiry_days,
      hr_cover_note: link.hr_cover_note,
      is_staff_preview: isStaffPreview,
      included_artefacts: link.included_artefacts || [],
      cfg: {
        transcript_visible: isStaffPreview || hasTranscript,
        code_editor_visible: false,
        mcq_percentile_min_pool: 100,
      },
    },
  };
}
